import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import useArtistPage from '../hooks/useArtistPage';
import useEditArtistIdentity from '../hooks/useEditArtistIdentity';
import useAppContext from '../../../hooks/useAppContext';
import useUserMutation from '../../SignUp/hooks/useUserMutation';
import ArtistIdentityEdit from './ArtistIdentityEdit';
import ArtistIdentityCard from './ArtistIdentityCard';
import CustomSectionsCard from './CustomSectionsCard';
import MyClockCard from './MyClockCard';
import CustomSectionsView from './CustomSectionsView';
import { getCoverBackground } from '../consts';
import { Section } from '../types';
import useArtistProfileTemplates from '../hooks/useArtistProfileTemplates';
import useDominantColor from '../hooks/useDominantColor';
import { useDragAndDrop } from '../../../hooks/useDragAndDrop';

type ArtistPageView = 'overview' | 'sections';

const ArtistPage = () => {
  const { spacing } = useTheme();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState<ArtistPageView>('overview');

  useEffect(() => {
    setCurrentView('overview');
  }, [location.key]);

  const {
    name,
    shortDescription,
    photoUrl,
    coverUrl,
    description,
    countryName,
    socialMedia,
  } = useArtistPage();

  const { user, setUser } = useAppContext();
  const savedSections = useMemo(
    () => user?.artistProfile?.customSections ?? [],
    [user?.artistProfile?.customSections],
  );
  const editIdentity = useEditArtistIdentity();

  const editIdentityRef = useRef(editIdentity);
  editIdentityRef.current = editIdentity;

  useEffect(() => {
    if (
      user &&
      searchParams.get('edit') === 'true' &&
      !editIdentityRef.current.isEditing
    ) {
      editIdentityRef.current.handleStartEditing({
        showErrors: true,
        requireAllFields: true,
      });
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [user, searchParams, setSearchParams]);

  const { templates } = useArtistProfileTemplates();
  const dominantColor = useDominantColor(!coverUrl ? photoUrl : undefined);
  const coverBackground = getCoverBackground(
    coverUrl,
    dominantColor,
    'var(--background-default, #0C0C0C)',
  );

  const { mutate: updateUser } = useUserMutation(user?.id, {
    optimistic: false,
  });

  const handleSectionOrderChange = (reorderedSections: Section[]) => {
    const previousSections = savedSections;
    setUser((previousUser) =>
      previousUser
        ? {
            ...previousUser,
            artistProfile: {
              ...previousUser.artistProfile,
              customSections: reorderedSections,
            },
          }
        : previousUser,
    );
    updateUser(
      {
        artistProfile: {
          ...user?.artistProfile,
          customSections: reorderedSections,
        },
      },
      {
        onError: () => {
          setUser((previousUser) =>
            previousUser
              ? {
                  ...previousUser,
                  artistProfile: {
                    ...previousUser.artistProfile,
                    customSections: previousSections,
                  },
                }
              : previousUser,
          );
        },
      },
    );
  };

  const {
    displayItems: displaySections,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    draggedItem,
  } = useDragAndDrop({
    items: savedSections,
    onOrderChange: handleSectionOrderChange,
  });

  const isArtist = user?.type === 'Artist';

  if (currentView === 'sections') {
    return <CustomSectionsView onBack={() => setCurrentView('overview')} />;
  }

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Artist Identity */}
      <DSBox sx={{ pb: spacing(3) }}>
        {editIdentity.isEditing ? (
          <ArtistIdentityEdit {...editIdentity} />
        ) : (
          <ArtistIdentityCard
            name={name}
            shortDescription={shortDescription}
            photoUrl={photoUrl}
            coverUrl={coverUrl}
            description={description}
            countryName={countryName}
            socialMedia={socialMedia}
            coverBackground={coverBackground}
            onEdit={() => editIdentity.handleStartEditing()}
          />
        )}
      </DSBox>

      {/* My Clock */}
      <MyClockCard />

      {/* Custom Sections */}
      {isArtist && (
        <CustomSectionsCard
          savedSections={savedSections}
          displaySections={displaySections}
          templates={templates}
          onClick={() => setCurrentView('sections')}
          handleDragStart={handleDragStart}
          handleDragEnd={handleDragEnd}
          handleDragOver={handleDragOver}
          handleDragEnter={handleDragEnter}
          draggedItem={draggedItem}
        />
      )}
    </DSBox>
  );
};

export default ArtistPage;
